import React from 'react';
import './App.css';
import { AuthContext } from './Components/context/auth-context';
import { Projects } from './Components/admin/projects/Projects';
import SignIn from './Components/Login';
import { Nav } from './Components/Nav';
import { useAuth } from './Hooks/auth-hook';
import useProjectDocumentsStatus from './Hooks/useProjectDocumentsStatus';
import Logger from './utils/logger';
import { Timecard } from './Components/timecards/Timecard';
import { TimecardDownload } from './Components/timecards/TimecardDownload';
import { Employees } from './Components/admin/employees/employees';
import { Packages } from './Components/admin/packages/Packages';
import { ChangePassword } from './Components/admin/employees/ChangePassword';
import PackageWorkflow from './Components/workflow/PackageWorkflow';
import ProductionMetricsDashboard from './Components/Analytics/ProductionMetrics/ProductionMetricsDashboard';
import Analytics from './Components/Analytics/Analytics';
import WorkflowTemplatePage from './Components/admin/workflow-template/WorkflowTemplatePage';
import ProjectDocumentsAssistantPage from './Components/ai/project-docs/ProjectDocumentsAssistantPage';
import UiKitPage from './Components/ui/UiKitPage';
import AllBidsPage from './Components/bids/AllBidsPage';
import BidOverviewPage from './Components/bids/BidOverviewPage';
import { canAccessBids } from './utils/bidsAccess';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// The app's normal not-found page (also used while Project Documents is off).
const notFound = (
  <main style={{ padding: '1rem' }}>
    <p>There's nothing here!</p>
  </main>
);

const App = () => {
  const {
    token,
    login,
    logout,
    userId,
    employeeId,
    userRecordId,
    userEmail,
    userFirstName,
    userLastName,
    isAdmin,
    isTimecard,
    isWorkflow,
    isAnalytics,
    isSuperAdmin,
    homeUnion,
    resetPass,
    setResetPass,
  } = useAuth();

  if (token) {
    Logger.debug('App initialized with token', {
      resetPass,
      isAdmin,
      isTimecard,
      isWorkflow,
      isAnalytics,
      isSuperAdmin,
    });
  } else {
    Logger.debug('App initialized without token');
  }

  // Project Documents (#250): hidden until the server confirms it is on for
  // this super admin's session; off or failed -> not found. While the answer
  // is still on its way the Assistant's address shows nothing, not the
  // not-found page.
  const { enabled: projectDocumentsEnabled, pending: projectDocumentsPending } =
    useProjectDocumentsStatus({
      token,
      userId,
      isSuperAdmin,
    });

  const canAccessTimecards = isAdmin || isTimecard;
  const canAccessWorkflow = isAdmin || isWorkflow;
  const canAccessAnalytics = isAdmin || isAnalytics;
  const hasBidsAccess = canAccessBids({ isSuperAdmin });

  return (
    <div className="App">
      <AuthContext.Provider
        value={{
          isLoggedIn: !!token,
          token: token,
          userId: userId,
          employeeId: employeeId,
          userRecordId: userRecordId,
          login: login,
          logout: logout,
          userEmail: userEmail,
          userFirstName: userFirstName,
          userLastName: userLastName,
          isAdmin: isAdmin,
          isTimecard: isTimecard,
          isWorkflow: isWorkflow,
          isAnalytics: isAnalytics,
          isSuperAdmin: isSuperAdmin,
          projectDocumentsEnabled: projectDocumentsEnabled,
          homeUnion: homeUnion,
          resetPass: resetPass,
          setResetPassword: setResetPass,
        }}
      >
        <BrowserRouter>
          {/* {token && <SideNav />} */}

          <main>
            <Routes>
              <Route
                path="/"
                element={
                  token ? (
                    resetPass ? (
                      <ChangePassword />
                    ) : (
                      <div>
                        <Nav />
                      </div>
                    )
                  ) : (
                    <SignIn />
                  )
                }
              >
                {/* Default route - redirect based on role */}
                <Route
                  index
                  element={
                    isAdmin ? (
                      <Navigate replace to="/projects" />
                    ) : isTimecard ? (
                      <Navigate replace to="/timecards" />
                    ) : isWorkflow ? (
                      <Navigate replace to="/package-workflow" />
                    ) : isAnalytics ? (
                      <Navigate replace to="/analytics" />
                    ) : (
                      <Navigate replace to="/projects" />
                    )
                  }
                />
                {/* Admin-only routes */}
                <Route
                  path="projects"
                  element={isAdmin ? <Projects /> : <Navigate replace to="/" />}
                />
                <Route
                  path="packages"
                  element={isAdmin ? <Packages /> : <Navigate replace to="/" />}
                />
                <Route
                  path="employees"
                  element={
                    isAdmin ? <Employees /> : <Navigate replace to="/" />
                  }
                />
                <Route
                  path="workflow-template"
                  element={
                    isSuperAdmin ? (
                      <WorkflowTemplatePage />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route
                  path="project-documents-assistant"
                  element={
                    isSuperAdmin && projectDocumentsEnabled ? (
                      <ProjectDocumentsAssistantPage />
                    ) : projectDocumentsPending ? null : (
                      notFound
                    )
                  }
                />
                <Route
                  path="analytics"
                  element={
                    canAccessAnalytics ? (
                      <Analytics />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                {/* Foreman and Admin routes */}
                <Route
                  path="timecards"
                  element={
                    canAccessTimecards ? (
                      <Timecard />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route
                  path="reports"
                  element={
                    canAccessTimecards ? (
                      <TimecardDownload />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route path="changepassword" element={<ChangePassword />} />
                <Route
                  path="package-workflow"
                  element={
                    token && canAccessWorkflow ? (
                      <PackageWorkflow />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route
                  path="analytics/production-metrics"
                  element={
                    canAccessAnalytics ? (
                      <ProductionMetricsDashboard />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                {/* Bids workspace (#295) — super-admins, behind REACT_APP_FEATURE_BIDS */}
                <Route
                  path="bids"
                  element={
                    hasBidsAccess ? (
                      <AllBidsPage />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route
                  path="bids/overview"
                  element={
                    hasBidsAccess ? (
                      <BidOverviewPage />
                    ) : (
                      <Navigate replace to="/" />
                    )
                  }
                />
                <Route
                  path="dev/ui-kit"
                  element={
                    isSuperAdmin ? <UiKitPage /> : <Navigate replace to="/" />
                  }
                />
                <Route path="*" element={notFound} />
              </Route>
            </Routes>
          </main>
        </BrowserRouter>
      </AuthContext.Provider>
    </div>
  );
};

export default App;
